import { PaginatedResponse, useApi } from '@/api';
import { Link, TableCellBottom, EntityList } from '@/components';
import {
  CurrencyValue,
  Flex,
  RelativeDateTime,
  WalletAddress,
  InfinityIcon,
  CryptoIcon,
  useTableProps,
  TableSkeleton,
  DataTable
} from '@blockanalitica/ui';
import {
  TableColumn,
  TableData
} from '@blockanalitica/ui/package/types/src/components/molecules/DataTable';
import { useParams } from 'react-router';
import { TablePagination } from '@blockanalitica/ui/package/types/src/components/atoms/Table/types';
import { useTheme } from 'styled-components';

type Wallet = {
  wallet_address: string;
  total_supply: string;
  total_supply_change: string | null;
  total_supply_change_percentage: string | null;
  total_borrow: string;
  total_borrow_change: string | null;
  total_borrow_change_percentage: string | null;
  net: string;
  liquidation_buffer: string;
  liquidation_buffer_change: string | null;
  liquidation_buffer_change_percentage: string | null;
  account_liquidity: string;
  account_liquidity_change: string | null;
  account_liquidity_change_percentage: string | null;
  health_rate: number;
  health_rate_change: string | null;
  health_rate_change_percentage: string | null;
  emode: number;
  last_activity: string;
  supplied_assets: { symbol: string; address: string; network: string }[];
  borrowed_assets: { symbol: string; address: string; network: string }[];
};

type Asset = {
  address: string;
  symbol: string;
  network: string;
};

type WalletsTableParams = {
  url: string;
  tableFilters: {
    supplyAssetsFiltered: string | string[];
    borrowAssetsFiltered: string | string[];
  };
  textSearch: string;
  pagination: TablePagination;
};

export default function WalletsTable({
  url,
  tableFilters,
  textSearch,
  pagination
}: WalletsTableParams) {
  const theme = useTheme();
  const { network } = useParams();

  const { sorting } = useTableProps({
    initialSorting: '-total_borrow',
    filtersDebounceTime: 500,
    initialFilters: {
      textSearch: ''
    }
  });

  const [data, error] = useApi<PaginatedResponse<Wallet[]>>({
    url: url,
    queryParams: {
      page: pagination.currentPage,
      limit: pagination.pageSize,
      sort: sorting.currentSorting,
      search: textSearch,
      supply_assets: tableFilters.supplyAssetsFiltered.toString(),
      borrow_assets: tableFilters.borrowAssetsFiltered.toString()
    }
  });

  if (error) {
    throw error;
  }

  if (!data) {
    return <TableSkeleton rows={10} columns={6} />;
  }

  const columns: TableColumn<Wallet>[] = [
    {
      header: '',
      getCell: (row) => (
        <Flex gap={['xs']} alignItems={'center'}>
          <WalletAddress address={row.wallet_address} />
        </Flex>
      )
    },
    {
      header: 'Supplied Assets',
      getCell: (row) =>
        row.supplied_assets.length ? (
          <EntityList showTotalNumber={false}>
            {row.supplied_assets.map((asset: Asset) => {
              return (
                <CryptoIcon
                  key={asset.address}
                  name={asset.symbol}
                  address={asset.address}
                  size="3"
                  network={network}
                />
              );
            })}
          </EntityList>
        ) : (
          []
        ),
      cellAlign: 'flex-end',
      headerAlign: 'flex-end'
    },
    {
      header: 'Supplied',
      getCell: (row) => (
        <CurrencyValue value={row.total_supply} options={{ currency: 'USD' }} />
      ),
      getCellBottom: (row) => (
        <TableCellBottom checkValue={row.total_supply_change}>
          <CurrencyValue
            value={row.total_supply_change}
            options={{
              trend: true,
              currency: 'USD'
            }}
          />
        </TableCellBottom>
      ),
      cellAlign: 'flex-end',
      headerAlign: 'flex-end',
      sortingField: 'total_supply'
    },
    {
      header: 'Borrowed Assets',
      getCell: (row) =>
        row.borrowed_assets.length ? (
          <EntityList showTotalNumber={false}>
            {row.borrowed_assets.map((asset: Asset) => {
              return (
                <CryptoIcon
                  key={asset.address}
                  name={asset.symbol}
                  address={asset.address}
                  size="3"
                  network={network}
                />
              );
            })}
          </EntityList>
        ) : (
          []
        ),
      cellAlign: 'flex-end',
      headerAlign: 'flex-end'
    },
    {
      header: 'Borrowed',
      getCell: (row) => (
        <CurrencyValue value={row.total_borrow} options={{ currency: 'USD' }} />
      ),
      getCellBottom: (row) => (
        <TableCellBottom checkValue={row.total_borrow_change}>
          <CurrencyValue
            value={row.total_borrow_change}
            options={{
              trend: true,
              currency: 'USD'
            }}
          />
        </TableCellBottom>
      ),
      cellAlign: 'flex-end',
      headerAlign: 'flex-end',
      sortingField: 'total_borrow'
    },
    {
      header: 'Health Rate',
      getCell: (row) => (
        <CurrencyValue
          value={row.health_rate}
          options={{
            nullable: '-',
            infinityThreshold: 1000,
            infinityIcon: (
              <InfinityIcon
                width={theme.sizes['4']}
                height={theme.sizes['2']}
              />
            )
          }}
        />
      ),
      cellAlign: 'flex-end',
      headerAlign: 'flex-end',
      sortingField: 'sortable_health_rate'
    },
    {
      header: 'Last Activity',
      getCell: (row) => <RelativeDateTime value={row.last_activity} />,
      cellAlign: 'flex-end',
      headerAlign: 'flex-end',
      sortingField: 'last_activity'
    }
  ];

  return (
    <DataTable
      rows={data.results}
      columns={columns}
      getKey={(row) => row.wallet_address}
      rowLinkProps={{
        to: (row: TableData) => `/wallets/${row.wallet_address}`,
        // @ts-ignore
        linkComponent: Link
      }}
      sorting={sorting}
      pagination={{
        ...pagination,
        totalRecords: data.pagination.total
      }}
    />
  );
}
